// Dealer Portal — Change Requests view i18n pack
//
// Zorzutti CR 05/08/2026 — the polish dealer telcatestpl@telcavoip.eu
// received a fully-Italian "Richieste modifica" page even though his
// portal UI language was Polish. Root cause: DealerChangeRequestsView
// was 100% hardcoded in Italian (no ``t.xxx`` lookups) and the
// portal tab label ``Richieste modifica`` was also literal Italian.
//
// This module holds the 40 keys required by that view + the tab label,
// translated for the full set of 25 EU languages we support. It is
// merged into the dealer translation bundle by ``getDealerTranslation``
// with a UK-first shallow-merge, so any key missing from a language
// falls back to English (never Italian — mirrors the email pack
// policy after the 03/08/2026 leak fix).

const CHANGE_REQUESTS_I18N = {
  IT: {
    changeRequestsTab: "Richieste modifica",
    crTitle: "Richieste modifica dati clienti",
    crSubtitle: "I clienti hanno richiesto la modifica di campi bloccati. Approva o rifiuta.",
    crLoadError: "Errore caricamento richieste",
    crApprovedToast: "Richiesta approvata: dati cliente aggiornati",
    crApproveError: "Errore approvazione",
    crRejectedToast: "Richiesta rifiutata",
    crRejectError: "Errore rifiuto",
    crConfirmApply: 'Applicare "{value}" a {field} del cliente {name}?',
    crDeepLinkGone: "La richiesta collegata dall'email non è più in attesa: potrebbe essere già stata approvata o rifiutata.",
    crDeepLinkBanner: "Aperta direttamente dall'email di notifica",
    crEmpty: "Nessuna richiesta in sospeso",
    crEmptyHint: "Le richieste dei clienti appariranno qui.",
    crFieldLabelEmail: "Email",
    crFieldLabelPhone: "Cellulare 1",
    crFieldLabelTaxCode: "Codice Fiscale",
    crFieldSection: "Campo",
    crCurrentValue: "Valore attuale",
    crNewValueRequested: "Nuovo valore richiesto",
    crCustomerNote: "Motivazione del cliente",
    crRejectionPlaceholder: "Motivazione rifiuto (opzionale)",
    crRejectBtn: "Rifiuta",
    crApproveBtn: "Approva",
    crApproveResendBtn: "Approva e reinvia email",
    crApproveWarning: "Approvando aggiornerai il campo {field} del cliente e verrà inviata AUTOMATICAMENTE una nuova email di attivazione al {target}. Il vecchio link resterà inutilizzabile.",
    crApproveWarningTargetEmail: "nuovo indirizzo",
    crApproveWarningTargetCustomer: "cliente",
    crPreSigBadge: "Pre-firma · Urgente",
    crPreSigCountOne: "{n} richiesta — clienti in attesa di ripartire",
    crPreSigCountMany: "{n} richieste — clienti in attesa di ripartire",
    crPostSigBadge: "Post-firma",
    crPostSigCountOne: "{n} richiesta — contratto già firmato",
    crPostSigCountMany: "{n} richieste — contratto già firmato",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA sforata",
    crSlaWarnAggregate: "{n} in attenzione (>{h}h)",
    crSlaOkAggregate: "{n} nei tempi",
    crSlaBadgeBreach: "SLA sforata · {h}h dall'invio",
    crSlaBadgeWarn: "Attenzione · {h}h · scade in {rem}h",
    crSlaBadgeOk: "Nei tempi · {h}h · {rem}h alla scadenza",
    crDateTimeLocale: "it-IT",
  },
  UK: {
    changeRequestsTab: "Change Requests",
    crTitle: "Customer data change requests",
    crSubtitle: "Customers have requested changes to locked fields. Approve or reject.",
    crLoadError: "Error loading requests",
    crApprovedToast: "Request approved: customer data updated",
    crApproveError: "Error approving",
    crRejectedToast: "Request rejected",
    crRejectError: "Error rejecting",
    crConfirmApply: 'Apply "{value}" to {field} for customer {name}?',
    crDeepLinkGone: "The request linked from the email is no longer pending: it may have already been approved or rejected.",
    crDeepLinkBanner: "Opened directly from notification email",
    crEmpty: "No pending requests",
    crEmptyHint: "Customer requests will appear here.",
    crFieldLabelEmail: "Email",
    crFieldLabelPhone: "Mobile phone 1",
    crFieldLabelTaxCode: "Tax code",
    crFieldSection: "Field",
    crCurrentValue: "Current value",
    crNewValueRequested: "New requested value",
    crCustomerNote: "Customer's motivation",
    crRejectionPlaceholder: "Rejection reason (optional)",
    crRejectBtn: "Reject",
    crApproveBtn: "Approve",
    crApproveResendBtn: "Approve and resend email",
    crApproveWarning: "Approving will update the customer's {field} field and a new activation email will be sent AUTOMATICALLY to the {target}. The old link will become unusable.",
    crApproveWarningTargetEmail: "new address",
    crApproveWarningTargetCustomer: "customer",
    crPreSigBadge: "Pre-signature · Urgent",
    crPreSigCountOne: "{n} request — customers waiting to resume",
    crPreSigCountMany: "{n} requests — customers waiting to resume",
    crPostSigBadge: "Post-signature",
    crPostSigCountOne: "{n} request — contract already signed",
    crPostSigCountMany: "{n} requests — contract already signed",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA breached",
    crSlaWarnAggregate: "{n} in warning (>{h}h)",
    crSlaOkAggregate: "{n} on time",
    crSlaBadgeBreach: "SLA breached · {h}h since submission",
    crSlaBadgeWarn: "Warning · {h}h · expires in {rem}h",
    crSlaBadgeOk: "On time · {h}h · {rem}h to deadline",
    crDateTimeLocale: "en-GB",
  },
  FR: {
    changeRequestsTab: "Demandes de modification",
    crTitle: "Demandes de modification des données clients",
    crSubtitle: "Les clients ont demandé la modification de champs verrouillés. Approuver ou rejeter.",
    crLoadError: "Erreur de chargement des demandes",
    crApprovedToast: "Demande approuvée : données client mises à jour",
    crApproveError: "Erreur lors de l'approbation",
    crRejectedToast: "Demande rejetée",
    crRejectError: "Erreur lors du rejet",
    crConfirmApply: 'Appliquer « {value} » à {field} pour le client {name} ?',
    crDeepLinkGone: "La demande liée depuis l'e-mail n'est plus en attente : elle a peut-être déjà été approuvée ou rejetée.",
    crDeepLinkBanner: "Ouverte directement depuis l'e-mail de notification",
    crEmpty: "Aucune demande en attente",
    crEmptyHint: "Les demandes des clients apparaîtront ici.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Téléphone portable 1",
    crFieldLabelTaxCode: "Code fiscal",
    crFieldSection: "Champ",
    crCurrentValue: "Valeur actuelle",
    crNewValueRequested: "Nouvelle valeur demandée",
    crCustomerNote: "Motivation du client",
    crRejectionPlaceholder: "Motif du rejet (facultatif)",
    crRejectBtn: "Rejeter",
    crApproveBtn: "Approuver",
    crApproveResendBtn: "Approuver et renvoyer l'e-mail",
    crApproveWarning: "En approuvant, vous mettrez à jour le champ {field} du client et un nouvel e-mail d'activation sera envoyé AUTOMATIQUEMENT à {target}. L'ancien lien deviendra inutilisable.",
    crApproveWarningTargetEmail: "la nouvelle adresse",
    crApproveWarningTargetCustomer: "au client",
    crPreSigBadge: "Pré-signature · Urgent",
    crPreSigCountOne: "{n} demande — clients en attente de reprise",
    crPreSigCountMany: "{n} demandes — clients en attente de reprise",
    crPostSigBadge: "Post-signature",
    crPostSigCountOne: "{n} demande — contrat déjà signé",
    crPostSigCountMany: "{n} demandes — contrat déjà signé",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA dépassé",
    crSlaWarnAggregate: "{n} en alerte (>{h}h)",
    crSlaOkAggregate: "{n} dans les délais",
    crSlaBadgeBreach: "SLA dépassé · {h}h depuis l'envoi",
    crSlaBadgeWarn: "Attention · {h}h · expire dans {rem}h",
    crSlaBadgeOk: "Dans les délais · {h}h · {rem}h avant échéance",
    crDateTimeLocale: "fr-FR",
  },
  ES: {
    changeRequestsTab: "Solicitudes de modificación",
    crTitle: "Solicitudes de modificación de datos de clientes",
    crSubtitle: "Los clientes han solicitado modificar campos bloqueados. Apruebe o rechace.",
    crLoadError: "Error al cargar las solicitudes",
    crApprovedToast: "Solicitud aprobada: datos del cliente actualizados",
    crApproveError: "Error al aprobar",
    crRejectedToast: "Solicitud rechazada",
    crRejectError: "Error al rechazar",
    crConfirmApply: '¿Aplicar "{value}" a {field} del cliente {name}?',
    crDeepLinkGone: "La solicitud vinculada desde el correo ya no está pendiente: es posible que ya haya sido aprobada o rechazada.",
    crDeepLinkBanner: "Abierta directamente desde el correo de notificación",
    crEmpty: "No hay solicitudes pendientes",
    crEmptyHint: "Las solicitudes de los clientes aparecerán aquí.",
    crFieldLabelEmail: "Correo electrónico",
    crFieldLabelPhone: "Teléfono móvil 1",
    crFieldLabelTaxCode: "Código fiscal",
    crFieldSection: "Campo",
    crCurrentValue: "Valor actual",
    crNewValueRequested: "Nuevo valor solicitado",
    crCustomerNote: "Motivo del cliente",
    crRejectionPlaceholder: "Motivo del rechazo (opcional)",
    crRejectBtn: "Rechazar",
    crApproveBtn: "Aprobar",
    crApproveResendBtn: "Aprobar y reenviar correo",
    crApproveWarning: "Al aprobar, actualizará el campo {field} del cliente y se enviará AUTOMÁTICAMENTE un nuevo correo de activación a {target}. El enlace anterior dejará de funcionar.",
    crApproveWarningTargetEmail: "la nueva dirección",
    crApproveWarningTargetCustomer: "el cliente",
    crPreSigBadge: "Pre-firma · Urgente",
    crPreSigCountOne: "{n} solicitud — clientes en espera de continuar",
    crPreSigCountMany: "{n} solicitudes — clientes en espera de continuar",
    crPostSigBadge: "Post-firma",
    crPostSigCountOne: "{n} solicitud — contrato ya firmado",
    crPostSigCountMany: "{n} solicitudes — contrato ya firmado",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA superado",
    crSlaWarnAggregate: "{n} en alerta (>{h}h)",
    crSlaOkAggregate: "{n} a tiempo",
    crSlaBadgeBreach: "SLA superado · {h}h desde el envío",
    crSlaBadgeWarn: "Atención · {h}h · vence en {rem}h",
    crSlaBadgeOk: "A tiempo · {h}h · {rem}h para el vencimiento",
    crDateTimeLocale: "es-ES",
  },
  DE: {
    changeRequestsTab: "Änderungsanfragen",
    crTitle: "Änderungsanfragen zu Kundendaten",
    crSubtitle: "Kunden haben Änderungen an gesperrten Feldern angefragt. Genehmigen oder ablehnen.",
    crLoadError: "Fehler beim Laden der Anfragen",
    crApprovedToast: "Anfrage genehmigt: Kundendaten aktualisiert",
    crApproveError: "Fehler bei der Genehmigung",
    crRejectedToast: "Anfrage abgelehnt",
    crRejectError: "Fehler bei der Ablehnung",
    crConfirmApply: '„{value}" auf {field} von Kunde {name} anwenden?',
    crDeepLinkGone: "Die aus der E-Mail verlinkte Anfrage ist nicht mehr offen: sie wurde möglicherweise bereits genehmigt oder abgelehnt.",
    crDeepLinkBanner: "Direkt aus der Benachrichtigungs-E-Mail geöffnet",
    crEmpty: "Keine offenen Anfragen",
    crEmptyHint: "Kundenanfragen erscheinen hier.",
    crFieldLabelEmail: "E-Mail",
    crFieldLabelPhone: "Mobiltelefon 1",
    crFieldLabelTaxCode: "Steuernummer",
    crFieldSection: "Feld",
    crCurrentValue: "Aktueller Wert",
    crNewValueRequested: "Neuer angeforderter Wert",
    crCustomerNote: "Begründung des Kunden",
    crRejectionPlaceholder: "Ablehnungsgrund (optional)",
    crRejectBtn: "Ablehnen",
    crApproveBtn: "Genehmigen",
    crApproveResendBtn: "Genehmigen und E-Mail erneut senden",
    crApproveWarning: "Bei Genehmigung wird das Feld {field} des Kunden aktualisiert und eine neue Aktivierungs-E-Mail wird AUTOMATISCH an {target} gesendet. Der alte Link wird unbrauchbar.",
    crApproveWarningTargetEmail: "die neue Adresse",
    crApproveWarningTargetCustomer: "den Kunden",
    crPreSigBadge: "Vor-Unterschrift · Dringend",
    crPreSigCountOne: "{n} Anfrage — Kunden warten auf Fortsetzung",
    crPreSigCountMany: "{n} Anfragen — Kunden warten auf Fortsetzung",
    crPostSigBadge: "Nach-Unterschrift",
    crPostSigCountOne: "{n} Anfrage — Vertrag bereits unterschrieben",
    crPostSigCountMany: "{n} Anfragen — Vertrag bereits unterschrieben",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA überschritten",
    crSlaWarnAggregate: "{n} in Warnung (>{h}h)",
    crSlaOkAggregate: "{n} im Zeitplan",
    crSlaBadgeBreach: "SLA überschritten · {h}h seit Einreichung",
    crSlaBadgeWarn: "Achtung · {h}h · läuft ab in {rem}h",
    crSlaBadgeOk: "Im Zeitplan · {h}h · {rem}h bis Frist",
    crDateTimeLocale: "de-DE",
  },
  NL: {
    changeRequestsTab: "Wijzigingsverzoeken",
    crTitle: "Wijzigingsverzoeken klantgegevens",
    crSubtitle: "Klanten hebben wijzigingen aan vergrendelde velden aangevraagd. Goedkeuren of afwijzen.",
    crLoadError: "Fout bij laden van verzoeken",
    crApprovedToast: "Verzoek goedgekeurd: klantgegevens bijgewerkt",
    crApproveError: "Fout bij goedkeuren",
    crRejectedToast: "Verzoek afgewezen",
    crRejectError: "Fout bij afwijzen",
    crConfirmApply: '"{value}" toepassen op {field} voor klant {name}?',
    crDeepLinkGone: "Het via e-mail gekoppelde verzoek staat niet meer in de wachtrij: het is mogelijk al goedgekeurd of afgewezen.",
    crDeepLinkBanner: "Rechtstreeks geopend via notificatie-e-mail",
    crEmpty: "Geen openstaande verzoeken",
    crEmptyHint: "Klantverzoeken verschijnen hier.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Mobiele telefoon 1",
    crFieldLabelTaxCode: "Belastingnummer",
    crFieldSection: "Veld",
    crCurrentValue: "Huidige waarde",
    crNewValueRequested: "Nieuwe aangevraagde waarde",
    crCustomerNote: "Motivatie van de klant",
    crRejectionPlaceholder: "Reden voor afwijzing (optioneel)",
    crRejectBtn: "Afwijzen",
    crApproveBtn: "Goedkeuren",
    crApproveResendBtn: "Goedkeuren en e-mail opnieuw versturen",
    crApproveWarning: "Bij goedkeuring werkt u het veld {field} van de klant bij en wordt AUTOMATISCH een nieuwe activatie-e-mail verzonden naar {target}. De oude link wordt onbruikbaar.",
    crApproveWarningTargetEmail: "het nieuwe adres",
    crApproveWarningTargetCustomer: "de klant",
    crPreSigBadge: "Voor ondertekening · Dringend",
    crPreSigCountOne: "{n} verzoek — klanten wachten om te hervatten",
    crPreSigCountMany: "{n} verzoeken — klanten wachten om te hervatten",
    crPostSigBadge: "Na ondertekening",
    crPostSigCountOne: "{n} verzoek — contract al ondertekend",
    crPostSigCountMany: "{n} verzoeken — contract al ondertekend",
    crSlaLabel: "SLA 24u >",
    crSlaBreachAggregate: "{n} SLA overschreden",
    crSlaWarnAggregate: "{n} in waarschuwing (>{h}u)",
    crSlaOkAggregate: "{n} op tijd",
    crSlaBadgeBreach: "SLA overschreden · {h}u sinds indiening",
    crSlaBadgeWarn: "Waarschuwing · {h}u · verloopt over {rem}u",
    crSlaBadgeOk: "Op tijd · {h}u · {rem}u tot deadline",
    crDateTimeLocale: "nl-NL",
  },
  PT: {
    changeRequestsTab: "Pedidos de alteração",
    crTitle: "Pedidos de alteração de dados de clientes",
    crSubtitle: "Os clientes solicitaram alterações a campos bloqueados. Aprove ou rejeite.",
    crLoadError: "Erro ao carregar pedidos",
    crApprovedToast: "Pedido aprovado: dados do cliente atualizados",
    crApproveError: "Erro na aprovação",
    crRejectedToast: "Pedido rejeitado",
    crRejectError: "Erro na rejeição",
    crConfirmApply: 'Aplicar "{value}" a {field} do cliente {name}?',
    crDeepLinkGone: "O pedido ligado a partir do e-mail já não está pendente: pode já ter sido aprovado ou rejeitado.",
    crDeepLinkBanner: "Aberto diretamente a partir do e-mail de notificação",
    crEmpty: "Nenhum pedido pendente",
    crEmptyHint: "Os pedidos dos clientes aparecerão aqui.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Telemóvel 1",
    crFieldLabelTaxCode: "Número fiscal",
    crFieldSection: "Campo",
    crCurrentValue: "Valor atual",
    crNewValueRequested: "Novo valor solicitado",
    crCustomerNote: "Motivo do cliente",
    crRejectionPlaceholder: "Motivo da rejeição (opcional)",
    crRejectBtn: "Rejeitar",
    crApproveBtn: "Aprovar",
    crApproveResendBtn: "Aprovar e reenviar e-mail",
    crApproveWarning: "Ao aprovar, atualizará o campo {field} do cliente e será enviado AUTOMATICAMENTE um novo e-mail de ativação para {target}. O link antigo deixará de funcionar.",
    crApproveWarningTargetEmail: "o novo endereço",
    crApproveWarningTargetCustomer: "o cliente",
    crPreSigBadge: "Pré-assinatura · Urgente",
    crPreSigCountOne: "{n} pedido — clientes à espera de retomar",
    crPreSigCountMany: "{n} pedidos — clientes à espera de retomar",
    crPostSigBadge: "Pós-assinatura",
    crPostSigCountOne: "{n} pedido — contrato já assinado",
    crPostSigCountMany: "{n} pedidos — contrato já assinado",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA excedido",
    crSlaWarnAggregate: "{n} em alerta (>{h}h)",
    crSlaOkAggregate: "{n} no prazo",
    crSlaBadgeBreach: "SLA excedido · {h}h desde o envio",
    crSlaBadgeWarn: "Atenção · {h}h · expira em {rem}h",
    crSlaBadgeOk: "No prazo · {h}h · {rem}h até ao prazo",
    crDateTimeLocale: "pt-PT",
  },
  PL: {
    changeRequestsTab: "Wnioski o zmianę",
    crTitle: "Wnioski o zmianę danych klientów",
    crSubtitle: "Klienci poprosili o zmianę zablokowanych pól. Zatwierdź lub odrzuć.",
    crLoadError: "Błąd ładowania wniosków",
    crApprovedToast: "Wniosek zatwierdzony: dane klienta zaktualizowane",
    crApproveError: "Błąd zatwierdzania",
    crRejectedToast: "Wniosek odrzucony",
    crRejectError: "Błąd odrzucania",
    crConfirmApply: 'Zastosować „{value}" do {field} klienta {name}?',
    crDeepLinkGone: "Wniosek połączony z e-maila nie jest już oczekujący: mógł już zostać zatwierdzony lub odrzucony.",
    crDeepLinkBanner: "Otwarto bezpośrednio z e-maila powiadomienia",
    crEmpty: "Brak oczekujących wniosków",
    crEmptyHint: "Wnioski klientów pojawią się tutaj.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Telefon komórkowy 1",
    crFieldLabelTaxCode: "Numer podatkowy",
    crFieldSection: "Pole",
    crCurrentValue: "Bieżąca wartość",
    crNewValueRequested: "Nowa żądana wartość",
    crCustomerNote: "Uzasadnienie klienta",
    crRejectionPlaceholder: "Uzasadnienie odrzucenia (opcjonalne)",
    crRejectBtn: "Odrzuć",
    crApproveBtn: "Zatwierdź",
    crApproveResendBtn: "Zatwierdź i wyślij ponownie e-mail",
    crApproveWarning: "Zatwierdzenie zaktualizuje pole {field} klienta i AUTOMATYCZNIE zostanie wysłany nowy e-mail aktywacyjny do {target}. Stary link stanie się bezużyteczny.",
    crApproveWarningTargetEmail: "nowego adresu",
    crApproveWarningTargetCustomer: "klienta",
    crPreSigBadge: "Przed podpisem · Pilne",
    crPreSigCountOne: "{n} wniosek — klienci czekają na wznowienie",
    crPreSigCountMany: "{n} wniosków — klienci czekają na wznowienie",
    crPostSigBadge: "Po podpisie",
    crPostSigCountOne: "{n} wniosek — umowa już podpisana",
    crPostSigCountMany: "{n} wniosków — umowa już podpisana",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA przekroczony",
    crSlaWarnAggregate: "{n} w ostrzeżeniu (>{h}h)",
    crSlaOkAggregate: "{n} na czas",
    crSlaBadgeBreach: "SLA przekroczony · {h}h od wysłania",
    crSlaBadgeWarn: "Uwaga · {h}h · wygasa za {rem}h",
    crSlaBadgeOk: "Na czas · {h}h · {rem}h do terminu",
    crDateTimeLocale: "pl-PL",
  },
  SE: {
    changeRequestsTab: "Ändringsförfrågningar",
    crTitle: "Ändringsförfrågningar för kunddata",
    crSubtitle: "Kunder har begärt ändringar av låsta fält. Godkänn eller avvisa.",
    crLoadError: "Fel vid inläsning av förfrågningar",
    crApprovedToast: "Förfrågan godkänd: kunddata uppdaterade",
    crApproveError: "Fel vid godkännande",
    crRejectedToast: "Förfrågan avvisad",
    crRejectError: "Fel vid avvisning",
    crConfirmApply: 'Använda "{value}" på {field} för kund {name}?',
    crDeepLinkGone: "Förfrågan länkad från e-postmeddelandet är inte längre väntande: den kan redan ha godkänts eller avvisats.",
    crDeepLinkBanner: "Öppnad direkt från notifieringsmejlet",
    crEmpty: "Inga väntande förfrågningar",
    crEmptyHint: "Kundförfrågningar visas här.",
    crFieldLabelEmail: "E-post",
    crFieldLabelPhone: "Mobiltelefon 1",
    crFieldLabelTaxCode: "Skattekod",
    crFieldSection: "Fält",
    crCurrentValue: "Nuvarande värde",
    crNewValueRequested: "Nytt begärt värde",
    crCustomerNote: "Kundens motivering",
    crRejectionPlaceholder: "Anledning till avvisning (valfritt)",
    crRejectBtn: "Avvisa",
    crApproveBtn: "Godkänn",
    crApproveResendBtn: "Godkänn och skicka om e-post",
    crApproveWarning: "Vid godkännande uppdateras kundens {field}-fält och ett nytt aktiveringsmejl skickas AUTOMATISKT till {target}. Den gamla länken blir oanvändbar.",
    crApproveWarningTargetEmail: "den nya adressen",
    crApproveWarningTargetCustomer: "kunden",
    crPreSigBadge: "Före signering · Brådskande",
    crPreSigCountOne: "{n} förfrågan — kunder väntar på att fortsätta",
    crPreSigCountMany: "{n} förfrågningar — kunder väntar på att fortsätta",
    crPostSigBadge: "Efter signering",
    crPostSigCountOne: "{n} förfrågan — avtal redan signerat",
    crPostSigCountMany: "{n} förfrågningar — avtal redan signerat",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA överskriden",
    crSlaWarnAggregate: "{n} i varning (>{h}h)",
    crSlaOkAggregate: "{n} i tid",
    crSlaBadgeBreach: "SLA överskriden · {h}h sedan skickat",
    crSlaBadgeWarn: "Varning · {h}h · löper ut om {rem}h",
    crSlaBadgeOk: "I tid · {h}h · {rem}h till deadline",
    crDateTimeLocale: "sv-SE",
  },
  NO: {
    changeRequestsTab: "Endringsforespørsler",
    crTitle: "Endringsforespørsler for kundedata",
    crSubtitle: "Kunder har bedt om endringer av låste felt. Godkjenn eller avvis.",
    crLoadError: "Feil ved lasting av forespørsler",
    crApprovedToast: "Forespørsel godkjent: kundedata oppdatert",
    crApproveError: "Feil ved godkjenning",
    crRejectedToast: "Forespørsel avvist",
    crRejectError: "Feil ved avvisning",
    crConfirmApply: 'Bruke "{value}" på {field} for kunde {name}?',
    crDeepLinkGone: "Forespørselen fra e-posten er ikke lenger ventende: den kan allerede ha blitt godkjent eller avvist.",
    crDeepLinkBanner: "Åpnet direkte fra varsel-e-post",
    crEmpty: "Ingen ventende forespørsler",
    crEmptyHint: "Kundeforespørsler vises her.",
    crFieldLabelEmail: "E-post",
    crFieldLabelPhone: "Mobiltelefon 1",
    crFieldLabelTaxCode: "Skattenummer",
    crFieldSection: "Felt",
    crCurrentValue: "Nåværende verdi",
    crNewValueRequested: "Ny ønsket verdi",
    crCustomerNote: "Kundens begrunnelse",
    crRejectionPlaceholder: "Grunn til avvisning (valgfritt)",
    crRejectBtn: "Avvis",
    crApproveBtn: "Godkjenn",
    crApproveResendBtn: "Godkjenn og send e-post på nytt",
    crApproveWarning: "Ved godkjenning oppdateres kundens {field}-felt, og en ny aktiverings-e-post sendes AUTOMATISK til {target}. Den gamle lenken blir ubrukelig.",
    crApproveWarningTargetEmail: "den nye adressen",
    crApproveWarningTargetCustomer: "kunden",
    crPreSigBadge: "Før signering · Haster",
    crPreSigCountOne: "{n} forespørsel — kunder venter på å fortsette",
    crPreSigCountMany: "{n} forespørsler — kunder venter på å fortsette",
    crPostSigBadge: "Etter signering",
    crPostSigCountOne: "{n} forespørsel — kontrakt allerede signert",
    crPostSigCountMany: "{n} forespørsler — kontrakt allerede signert",
    crSlaLabel: "SLA 24t >",
    crSlaBreachAggregate: "{n} SLA overskredet",
    crSlaWarnAggregate: "{n} i advarsel (>{h}t)",
    crSlaOkAggregate: "{n} i tide",
    crSlaBadgeBreach: "SLA overskredet · {h}t siden sending",
    crSlaBadgeWarn: "Advarsel · {h}t · utløper om {rem}t",
    crSlaBadgeOk: "I tide · {h}t · {rem}t til frist",
    crDateTimeLocale: "nb-NO",
  },
  FI: {
    changeRequestsTab: "Muutospyynnöt",
    crTitle: "Asiakastietojen muutospyynnöt",
    crSubtitle: "Asiakkaat ovat pyytäneet muutoksia lukittuihin kenttiin. Hyväksy tai hylkää.",
    crLoadError: "Virhe ladattaessa pyyntöjä",
    crApprovedToast: "Pyyntö hyväksytty: asiakastiedot päivitetty",
    crApproveError: "Virhe hyväksynnässä",
    crRejectedToast: "Pyyntö hylätty",
    crRejectError: "Virhe hylkäämisessä",
    crConfirmApply: 'Sovelletaanko arvoa "{value}" kenttään {field} asiakkaalle {name}?',
    crDeepLinkGone: "Sähköpostissa oleva linkitetty pyyntö ei ole enää odottamassa: se on saatettu jo hyväksyä tai hylätä.",
    crDeepLinkBanner: "Avattu suoraan ilmoitussähköpostista",
    crEmpty: "Ei odottavia pyyntöjä",
    crEmptyHint: "Asiakaspyynnöt näkyvät täällä.",
    crFieldLabelEmail: "Sähköposti",
    crFieldLabelPhone: "Matkapuhelin 1",
    crFieldLabelTaxCode: "Verokoodi",
    crFieldSection: "Kenttä",
    crCurrentValue: "Nykyinen arvo",
    crNewValueRequested: "Uusi pyydetty arvo",
    crCustomerNote: "Asiakkaan perustelu",
    crRejectionPlaceholder: "Hylkäämisen syy (valinnainen)",
    crRejectBtn: "Hylkää",
    crApproveBtn: "Hyväksy",
    crApproveResendBtn: "Hyväksy ja lähetä sähköposti uudelleen",
    crApproveWarning: "Hyväksyminen päivittää asiakkaan {field}-kentän, ja uusi aktivointi-sähköposti lähetetään AUTOMAATTISESTI kohteeseen {target}. Vanha linkki muuttuu käyttökelvottomaksi.",
    crApproveWarningTargetEmail: "uusi osoite",
    crApproveWarningTargetCustomer: "asiakas",
    crPreSigBadge: "Ennen allekirjoitusta · Kiireellinen",
    crPreSigCountOne: "{n} pyyntö — asiakkaat odottavat jatkoa",
    crPreSigCountMany: "{n} pyyntöä — asiakkaat odottavat jatkoa",
    crPostSigBadge: "Allekirjoituksen jälkeen",
    crPostSigCountOne: "{n} pyyntö — sopimus jo allekirjoitettu",
    crPostSigCountMany: "{n} pyyntöä — sopimus jo allekirjoitettu",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA ylitetty",
    crSlaWarnAggregate: "{n} varoituksessa (>{h}h)",
    crSlaOkAggregate: "{n} ajoissa",
    crSlaBadgeBreach: "SLA ylitetty · {h}h lähetyksestä",
    crSlaBadgeWarn: "Varoitus · {h}h · vanhenee {rem}h kuluttua",
    crSlaBadgeOk: "Ajoissa · {h}h · {rem}h määräaikaan",
    crDateTimeLocale: "fi-FI",
  },
  DK: {
    changeRequestsTab: "Ændringsanmodninger",
    crTitle: "Ændringsanmodninger til kundedata",
    crSubtitle: "Kunder har anmodet om ændringer af låste felter. Godkend eller afvis.",
    crLoadError: "Fejl ved indlæsning af anmodninger",
    crApprovedToast: "Anmodning godkendt: kundedata opdateret",
    crApproveError: "Fejl ved godkendelse",
    crRejectedToast: "Anmodning afvist",
    crRejectError: "Fejl ved afvisning",
    crConfirmApply: 'Anvend "{value}" på {field} for kunde {name}?',
    crDeepLinkGone: "Anmodningen linket fra e-mailen er ikke længere afventende: den er muligvis allerede godkendt eller afvist.",
    crDeepLinkBanner: "Åbnet direkte fra notifikationsmail",
    crEmpty: "Ingen afventende anmodninger",
    crEmptyHint: "Kundeanmodninger vises her.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Mobiltelefon 1",
    crFieldLabelTaxCode: "Skattekode",
    crFieldSection: "Felt",
    crCurrentValue: "Aktuel værdi",
    crNewValueRequested: "Ny anmodet værdi",
    crCustomerNote: "Kundens begrundelse",
    crRejectionPlaceholder: "Årsag til afvisning (valgfrit)",
    crRejectBtn: "Afvis",
    crApproveBtn: "Godkend",
    crApproveResendBtn: "Godkend og gensend e-mail",
    crApproveWarning: "Ved godkendelse opdateres kundens {field}-felt, og en ny aktiverings-e-mail sendes AUTOMATISK til {target}. Det gamle link bliver ubrugeligt.",
    crApproveWarningTargetEmail: "den nye adresse",
    crApproveWarningTargetCustomer: "kunden",
    crPreSigBadge: "Før underskrift · Haster",
    crPreSigCountOne: "{n} anmodning — kunder venter på at fortsætte",
    crPreSigCountMany: "{n} anmodninger — kunder venter på at fortsætte",
    crPostSigBadge: "Efter underskrift",
    crPostSigCountOne: "{n} anmodning — kontrakt allerede underskrevet",
    crPostSigCountMany: "{n} anmodninger — kontrakt allerede underskrevet",
    crSlaLabel: "SLA 24t >",
    crSlaBreachAggregate: "{n} SLA overskredet",
    crSlaWarnAggregate: "{n} i advarsel (>{h}t)",
    crSlaOkAggregate: "{n} til tiden",
    crSlaBadgeBreach: "SLA overskredet · {h}t siden indsendelse",
    crSlaBadgeWarn: "Advarsel · {h}t · udløber om {rem}t",
    crSlaBadgeOk: "Til tiden · {h}t · {rem}t til frist",
    crDateTimeLocale: "da-DK",
  },
  CZ: {
    changeRequestsTab: "Žádosti o změnu",
    crTitle: "Žádosti o změnu údajů zákazníků",
    crSubtitle: "Zákazníci požádali o změnu uzamčených polí. Schvalte nebo zamítněte.",
    crLoadError: "Chyba při načítání žádostí",
    crApprovedToast: "Žádost schválena: údaje zákazníka aktualizovány",
    crApproveError: "Chyba při schvalování",
    crRejectedToast: "Žádost zamítnuta",
    crRejectError: "Chyba při zamítání",
    crConfirmApply: 'Použít „{value}" na {field} zákazníka {name}?',
    crDeepLinkGone: "Žádost odkazovaná z e-mailu již není čekající: pravděpodobně již byla schválena nebo zamítnuta.",
    crDeepLinkBanner: "Otevřeno přímo z oznamovacího e-mailu",
    crEmpty: "Žádné čekající žádosti",
    crEmptyHint: "Žádosti zákazníků se zobrazí zde.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Mobilní telefon 1",
    crFieldLabelTaxCode: "Daňové číslo",
    crFieldSection: "Pole",
    crCurrentValue: "Aktuální hodnota",
    crNewValueRequested: "Nová požadovaná hodnota",
    crCustomerNote: "Odůvodnění zákazníka",
    crRejectionPlaceholder: "Důvod zamítnutí (nepovinné)",
    crRejectBtn: "Zamítnout",
    crApproveBtn: "Schválit",
    crApproveResendBtn: "Schválit a znovu odeslat e-mail",
    crApproveWarning: "Schválením aktualizujete pole {field} zákazníka a AUTOMATICKY bude odeslán nový aktivační e-mail na {target}. Starý odkaz přestane fungovat.",
    crApproveWarningTargetEmail: "novou adresu",
    crApproveWarningTargetCustomer: "zákazníka",
    crPreSigBadge: "Před podpisem · Naléhavé",
    crPreSigCountOne: "{n} žádost — zákazníci čekají na pokračování",
    crPreSigCountMany: "{n} žádostí — zákazníci čekají na pokračování",
    crPostSigBadge: "Po podpisu",
    crPostSigCountOne: "{n} žádost — smlouva již podepsána",
    crPostSigCountMany: "{n} žádostí — smlouva již podepsána",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA překročeno",
    crSlaWarnAggregate: "{n} ve výstraze (>{h}h)",
    crSlaOkAggregate: "{n} v termínu",
    crSlaBadgeBreach: "SLA překročeno · {h}h od odeslání",
    crSlaBadgeWarn: "Pozor · {h}h · vyprší za {rem}h",
    crSlaBadgeOk: "V termínu · {h}h · {rem}h do uplynutí",
    crDateTimeLocale: "cs-CZ",
  },
  GR: {
    changeRequestsTab: "Αιτήματα τροποποίησης",
    crTitle: "Αιτήματα τροποποίησης δεδομένων πελατών",
    crSubtitle: "Οι πελάτες έχουν ζητήσει αλλαγές σε κλειδωμένα πεδία. Εγκρίνετε ή απορρίψτε.",
    crLoadError: "Σφάλμα κατά τη φόρτωση αιτημάτων",
    crApprovedToast: "Αίτημα εγκρίθηκε: τα στοιχεία του πελάτη ενημερώθηκαν",
    crApproveError: "Σφάλμα κατά την έγκριση",
    crRejectedToast: "Αίτημα απορρίφθηκε",
    crRejectError: "Σφάλμα κατά την απόρριψη",
    crConfirmApply: 'Εφαρμογή του "{value}" στο πεδίο {field} του πελάτη {name};',
    crDeepLinkGone: "Το αίτημα από το e-mail δεν είναι πλέον σε εκκρεμότητα: μπορεί να έχει ήδη εγκριθεί ή απορριφθεί.",
    crDeepLinkBanner: "Άνοιξε απευθείας από το e-mail ειδοποίησης",
    crEmpty: "Δεν υπάρχουν εκκρεμή αιτήματα",
    crEmptyHint: "Τα αιτήματα των πελατών θα εμφανιστούν εδώ.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Κινητό τηλέφωνο 1",
    crFieldLabelTaxCode: "Φορολογικός κωδικός",
    crFieldSection: "Πεδίο",
    crCurrentValue: "Τρέχουσα τιμή",
    crNewValueRequested: "Νέα ζητούμενη τιμή",
    crCustomerNote: "Αιτιολόγηση πελάτη",
    crRejectionPlaceholder: "Αιτιολογία απόρριψης (προαιρετικό)",
    crRejectBtn: "Απόρριψη",
    crApproveBtn: "Έγκριση",
    crApproveResendBtn: "Έγκριση και επανάληψη αποστολής e-mail",
    crApproveWarning: "Με την έγκριση θα ενημερωθεί το πεδίο {field} του πελάτη και θα σταλεί ΑΥΤΟΜΑΤΑ ένα νέο e-mail ενεργοποίησης στο {target}. Ο παλιός σύνδεσμος θα καταστεί άχρηστος.",
    crApproveWarningTargetEmail: "νέα διεύθυνση",
    crApproveWarningTargetCustomer: "πελάτη",
    crPreSigBadge: "Πριν την υπογραφή · Επείγον",
    crPreSigCountOne: "{n} αίτημα — πελάτες περιμένουν να συνεχίσουν",
    crPreSigCountMany: "{n} αιτήματα — πελάτες περιμένουν να συνεχίσουν",
    crPostSigBadge: "Μετά την υπογραφή",
    crPostSigCountOne: "{n} αίτημα — σύμβαση ήδη υπογεγραμμένη",
    crPostSigCountMany: "{n} αιτήματα — σύμβαση ήδη υπογεγραμμένη",
    crSlaLabel: "SLA 24ω >",
    crSlaBreachAggregate: "{n} SLA παραβιάστηκε",
    crSlaWarnAggregate: "{n} σε προειδοποίηση (>{h}ω)",
    crSlaOkAggregate: "{n} εντός χρόνου",
    crSlaBadgeBreach: "SLA παραβιάστηκε · {h}ω από την αποστολή",
    crSlaBadgeWarn: "Προσοχή · {h}ω · λήγει σε {rem}ω",
    crSlaBadgeOk: "Εντός χρόνου · {h}ω · {rem}ω μέχρι τη λήξη",
    crDateTimeLocale: "el-GR",
  },
  HU: {
    changeRequestsTab: "Módosítási kérelmek",
    crTitle: "Ügyféladatok módosítási kérelmei",
    crSubtitle: "Az ügyfelek zárolt mezők módosítását kérték. Hagyja jóvá vagy utasítsa el.",
    crLoadError: "Hiba a kérelmek betöltésekor",
    crApprovedToast: "Kérelem jóváhagyva: az ügyfél adatai frissítve",
    crApproveError: "Hiba a jóváhagyáskor",
    crRejectedToast: "Kérelem elutasítva",
    crRejectError: "Hiba az elutasításkor",
    crConfirmApply: 'Alkalmazza a(z) „{value}" értéket a(z) {field} mezőre {name} ügyfél számára?',
    crDeepLinkGone: "Az e-mailből hivatkozott kérelem már nem függőben van: valószínűleg már jóváhagyták vagy elutasították.",
    crDeepLinkBanner: "Közvetlenül az értesítő e-mailből megnyitva",
    crEmpty: "Nincs függőben lévő kérelem",
    crEmptyHint: "Az ügyfélkérelmek itt fognak megjelenni.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Mobiltelefon 1",
    crFieldLabelTaxCode: "Adószám",
    crFieldSection: "Mező",
    crCurrentValue: "Jelenlegi érték",
    crNewValueRequested: "Új kért érték",
    crCustomerNote: "Ügyfél indoklása",
    crRejectionPlaceholder: "Elutasítás oka (opcionális)",
    crRejectBtn: "Elutasítás",
    crApproveBtn: "Jóváhagyás",
    crApproveResendBtn: "Jóváhagyás és e-mail újraküldése",
    crApproveWarning: "A jóváhagyás frissíti az ügyfél {field} mezőjét, és egy új aktiváló e-mail AUTOMATIKUSAN elküldésre kerül a következő címre: {target}. A régi hivatkozás használhatatlanná válik.",
    crApproveWarningTargetEmail: "új cím",
    crApproveWarningTargetCustomer: "ügyfél",
    crPreSigBadge: "Aláírás előtt · Sürgős",
    crPreSigCountOne: "{n} kérelem — ügyfelek várnak a folytatásra",
    crPreSigCountMany: "{n} kérelem — ügyfelek várnak a folytatásra",
    crPostSigBadge: "Aláírás után",
    crPostSigCountOne: "{n} kérelem — a szerződést már aláírták",
    crPostSigCountMany: "{n} kérelem — a szerződést már aláírták",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA túllépve",
    crSlaWarnAggregate: "{n} figyelmeztetésben (>{h}h)",
    crSlaOkAggregate: "{n} időben",
    crSlaBadgeBreach: "SLA túllépve · {h}h a beküldés óta",
    crSlaBadgeWarn: "Figyelem · {h}h · lejár {rem}h múlva",
    crSlaBadgeOk: "Időben · {h}h · {rem}h a határidőig",
    crDateTimeLocale: "hu-HU",
  },
  RO: {
    changeRequestsTab: "Cereri de modificare",
    crTitle: "Cereri de modificare a datelor clienților",
    crSubtitle: "Clienții au solicitat modificări la câmpuri blocate. Aprobați sau respingeți.",
    crLoadError: "Eroare la încărcarea cererilor",
    crApprovedToast: "Cerere aprobată: datele clientului actualizate",
    crApproveError: "Eroare la aprobare",
    crRejectedToast: "Cerere respinsă",
    crRejectError: "Eroare la respingere",
    crConfirmApply: 'Aplicați „{value}" pentru {field} al clientului {name}?',
    crDeepLinkGone: "Cererea legată din e-mail nu mai este în așteptare: este posibil să fi fost deja aprobată sau respinsă.",
    crDeepLinkBanner: "Deschisă direct din e-mailul de notificare",
    crEmpty: "Nicio cerere în așteptare",
    crEmptyHint: "Cererile clienților vor apărea aici.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Telefon mobil 1",
    crFieldLabelTaxCode: "Cod fiscal",
    crFieldSection: "Câmp",
    crCurrentValue: "Valoare actuală",
    crNewValueRequested: "Nouă valoare solicitată",
    crCustomerNote: "Motivația clientului",
    crRejectionPlaceholder: "Motivul respingerii (opțional)",
    crRejectBtn: "Respinge",
    crApproveBtn: "Aprobă",
    crApproveResendBtn: "Aprobă și retrimite e-mail",
    crApproveWarning: "Aprobarea va actualiza câmpul {field} al clientului și un nou e-mail de activare va fi trimis AUTOMAT către {target}. Vechea legătură va deveni inutilizabilă.",
    crApproveWarningTargetEmail: "noua adresă",
    crApproveWarningTargetCustomer: "client",
    crPreSigBadge: "Pre-semnare · Urgent",
    crPreSigCountOne: "{n} cerere — clienți în așteptare pentru a continua",
    crPreSigCountMany: "{n} cereri — clienți în așteptare pentru a continua",
    crPostSigBadge: "Post-semnare",
    crPostSigCountOne: "{n} cerere — contract deja semnat",
    crPostSigCountMany: "{n} cereri — contract deja semnat",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA depășit",
    crSlaWarnAggregate: "{n} în avertizare (>{h}h)",
    crSlaOkAggregate: "{n} la timp",
    crSlaBadgeBreach: "SLA depășit · {h}h de la trimitere",
    crSlaBadgeWarn: "Atenție · {h}h · expiră în {rem}h",
    crSlaBadgeOk: "La timp · {h}h · {rem}h până la termen",
    crDateTimeLocale: "ro-RO",
  },
  BG: {
    changeRequestsTab: "Заявки за промяна",
    crTitle: "Заявки за промяна на данни на клиенти",
    crSubtitle: "Клиентите поискаха промени в заключени полета. Одобрете или отхвърлете.",
    crLoadError: "Грешка при зареждане на заявките",
    crApprovedToast: "Заявката е одобрена: данните на клиента са актуализирани",
    crApproveError: "Грешка при одобрение",
    crRejectedToast: "Заявката е отхвърлена",
    crRejectError: "Грешка при отхвърляне",
    crConfirmApply: 'Приложете „{value}" към {field} за клиент {name}?',
    crDeepLinkGone: "Заявката, свързана от имейла, вече не е чакаща: вероятно вече е одобрена или отхвърлена.",
    crDeepLinkBanner: "Отворена директно от имейла за уведомление",
    crEmpty: "Няма чакащи заявки",
    crEmptyHint: "Заявките на клиентите ще се появят тук.",
    crFieldLabelEmail: "Имейл",
    crFieldLabelPhone: "Мобилен телефон 1",
    crFieldLabelTaxCode: "Данъчен код",
    crFieldSection: "Поле",
    crCurrentValue: "Текуща стойност",
    crNewValueRequested: "Нова заявена стойност",
    crCustomerNote: "Мотивация на клиента",
    crRejectionPlaceholder: "Причина за отхвърляне (по избор)",
    crRejectBtn: "Отхвърли",
    crApproveBtn: "Одобри",
    crApproveResendBtn: "Одобри и изпрати имейла отново",
    crApproveWarning: "Одобрението ще актуализира полето {field} на клиента и АВТОМАТИЧНО ще бъде изпратен нов имейл за активиране до {target}. Старият линк ще стане неизползваем.",
    crApproveWarningTargetEmail: "новия адрес",
    crApproveWarningTargetCustomer: "клиента",
    crPreSigBadge: "Преди подпис · Спешно",
    crPreSigCountOne: "{n} заявка — клиенти чакат да продължат",
    crPreSigCountMany: "{n} заявки — клиенти чакат да продължат",
    crPostSigBadge: "След подпис",
    crPostSigCountOne: "{n} заявка — договорът вече е подписан",
    crPostSigCountMany: "{n} заявки — договорът вече е подписан",
    crSlaLabel: "SLA 24ч >",
    crSlaBreachAggregate: "{n} SLA просрочен",
    crSlaWarnAggregate: "{n} в предупреждение (>{h}ч)",
    crSlaOkAggregate: "{n} в срок",
    crSlaBadgeBreach: "SLA просрочен · {h}ч от изпращането",
    crSlaBadgeWarn: "Внимание · {h}ч · изтича след {rem}ч",
    crSlaBadgeOk: "В срок · {h}ч · {rem}ч до крайния срок",
    crDateTimeLocale: "bg-BG",
  },
  HR: {
    changeRequestsTab: "Zahtjevi za izmjenu",
    crTitle: "Zahtjevi za izmjenu podataka klijenata",
    crSubtitle: "Klijenti su zatražili izmjene zaključanih polja. Odobrite ili odbijte.",
    crLoadError: "Pogreška pri učitavanju zahtjeva",
    crApprovedToast: "Zahtjev odobren: podaci klijenta ažurirani",
    crApproveError: "Pogreška pri odobravanju",
    crRejectedToast: "Zahtjev odbijen",
    crRejectError: "Pogreška pri odbijanju",
    crConfirmApply: 'Primijeniti „{value}" na {field} klijenta {name}?',
    crDeepLinkGone: "Zahtjev povezan iz e-pošte više nije na čekanju: vjerojatno je već odobren ili odbijen.",
    crDeepLinkBanner: "Otvoreno izravno iz obavijesnog e-maila",
    crEmpty: "Nema zahtjeva na čekanju",
    crEmptyHint: "Zahtjevi klijenata pojavit će se ovdje.",
    crFieldLabelEmail: "E-pošta",
    crFieldLabelPhone: "Mobitel 1",
    crFieldLabelTaxCode: "Porezni broj",
    crFieldSection: "Polje",
    crCurrentValue: "Trenutna vrijednost",
    crNewValueRequested: "Nova zatražena vrijednost",
    crCustomerNote: "Obrazloženje klijenta",
    crRejectionPlaceholder: "Razlog odbijanja (neobavezno)",
    crRejectBtn: "Odbij",
    crApproveBtn: "Odobri",
    crApproveResendBtn: "Odobri i ponovno pošalji e-poštu",
    crApproveWarning: "Odobrenjem će se ažurirati polje {field} klijenta i AUTOMATSKI će se poslati novi e-mail za aktivaciju na {target}. Stara poveznica postat će neupotrebljiva.",
    crApproveWarningTargetEmail: "novu adresu",
    crApproveWarningTargetCustomer: "klijenta",
    crPreSigBadge: "Prije potpisa · Hitno",
    crPreSigCountOne: "{n} zahtjev — klijenti čekaju nastavak",
    crPreSigCountMany: "{n} zahtjeva — klijenti čekaju nastavak",
    crPostSigBadge: "Nakon potpisa",
    crPostSigCountOne: "{n} zahtjev — ugovor već potpisan",
    crPostSigCountMany: "{n} zahtjeva — ugovor već potpisan",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA prekoračen",
    crSlaWarnAggregate: "{n} u upozorenju (>{h}h)",
    crSlaOkAggregate: "{n} u roku",
    crSlaBadgeBreach: "SLA prekoračen · {h}h od slanja",
    crSlaBadgeWarn: "Pozor · {h}h · istječe za {rem}h",
    crSlaBadgeOk: "U roku · {h}h · {rem}h do isteka",
    crDateTimeLocale: "hr-HR",
  },
  SI: {
    changeRequestsTab: "Zahteve za spremembo",
    crTitle: "Zahteve za spremembo podatkov strank",
    crSubtitle: "Stranke so zahtevale spremembe zaklenjenih polj. Odobrite ali zavrnite.",
    crLoadError: "Napaka pri nalaganju zahtev",
    crApprovedToast: "Zahteva odobrena: podatki stranke posodobljeni",
    crApproveError: "Napaka pri odobritvi",
    crRejectedToast: "Zahteva zavrnjena",
    crRejectError: "Napaka pri zavrnitvi",
    crConfirmApply: 'Uporabite „{value}" na {field} stranke {name}?',
    crDeepLinkGone: "Zahteva, povezana iz e-pošte, ni več v teku: morda je bila že odobrena ali zavrnjena.",
    crDeepLinkBanner: "Odprto neposredno iz obvestilnega e-poštnega sporočila",
    crEmpty: "Ni zahtev v čakanju",
    crEmptyHint: "Zahteve strank se bodo prikazale tukaj.",
    crFieldLabelEmail: "E-pošta",
    crFieldLabelPhone: "Mobilni telefon 1",
    crFieldLabelTaxCode: "Davčna številka",
    crFieldSection: "Polje",
    crCurrentValue: "Trenutna vrednost",
    crNewValueRequested: "Nova zahtevana vrednost",
    crCustomerNote: "Utemeljitev stranke",
    crRejectionPlaceholder: "Razlog za zavrnitev (neobvezno)",
    crRejectBtn: "Zavrni",
    crApproveBtn: "Odobri",
    crApproveResendBtn: "Odobri in ponovno pošlji e-pošto",
    crApproveWarning: "Z odobritvijo boste posodobili polje {field} stranke, novo aktivacijsko e-pošto pa bomo SAMODEJNO poslali na {target}. Stara povezava bo postala neuporabna.",
    crApproveWarningTargetEmail: "novi naslov",
    crApproveWarningTargetCustomer: "stranko",
    crPreSigBadge: "Pred podpisom · Nujno",
    crPreSigCountOne: "{n} zahteva — stranke čakajo na nadaljevanje",
    crPreSigCountMany: "{n} zahtev — stranke čakajo na nadaljevanje",
    crPostSigBadge: "Po podpisu",
    crPostSigCountOne: "{n} zahteva — pogodba že podpisana",
    crPostSigCountMany: "{n} zahtev — pogodba že podpisana",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA prekoračen",
    crSlaWarnAggregate: "{n} v opozorilu (>{h}h)",
    crSlaOkAggregate: "{n} v roku",
    crSlaBadgeBreach: "SLA prekoračen · {h}h od pošiljanja",
    crSlaBadgeWarn: "Pozor · {h}h · poteče čez {rem}h",
    crSlaBadgeOk: "V roku · {h}h · {rem}h do roka",
    crDateTimeLocale: "sl-SI",
  },
  SK: {
    changeRequestsTab: "Žiadosti o zmenu",
    crTitle: "Žiadosti o zmenu údajov zákazníkov",
    crSubtitle: "Zákazníci požiadali o zmeny uzamknutých polí. Schválte alebo zamietnite.",
    crLoadError: "Chyba pri načítaní žiadostí",
    crApprovedToast: "Žiadosť schválená: údaje zákazníka aktualizované",
    crApproveError: "Chyba pri schvaľovaní",
    crRejectedToast: "Žiadosť zamietnutá",
    crRejectError: "Chyba pri zamietaní",
    crConfirmApply: 'Použiť „{value}" na {field} zákazníka {name}?',
    crDeepLinkGone: "Žiadosť odkazovaná z e-mailu už nie je čakajúca: pravdepodobne bola už schválená alebo zamietnutá.",
    crDeepLinkBanner: "Otvorené priamo z oznamovacieho e-mailu",
    crEmpty: "Žiadne čakajúce žiadosti",
    crEmptyHint: "Žiadosti zákazníkov sa zobrazia tu.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Mobilný telefón 1",
    crFieldLabelTaxCode: "Daňové číslo",
    crFieldSection: "Pole",
    crCurrentValue: "Aktuálna hodnota",
    crNewValueRequested: "Nová požadovaná hodnota",
    crCustomerNote: "Odôvodnenie zákazníka",
    crRejectionPlaceholder: "Dôvod zamietnutia (voliteľné)",
    crRejectBtn: "Zamietnuť",
    crApproveBtn: "Schváliť",
    crApproveResendBtn: "Schváliť a znovu odoslať e-mail",
    crApproveWarning: "Schválením aktualizujete pole {field} zákazníka a AUTOMATICKY bude odoslaný nový aktivačný e-mail na {target}. Starý odkaz sa stane nepoužiteľným.",
    crApproveWarningTargetEmail: "novú adresu",
    crApproveWarningTargetCustomer: "zákazníka",
    crPreSigBadge: "Pred podpisom · Naliehavé",
    crPreSigCountOne: "{n} žiadosť — zákazníci čakajú na pokračovanie",
    crPreSigCountMany: "{n} žiadostí — zákazníci čakajú na pokračovanie",
    crPostSigBadge: "Po podpise",
    crPostSigCountOne: "{n} žiadosť — zmluva už podpísaná",
    crPostSigCountMany: "{n} žiadostí — zmluva už podpísaná",
    crSlaLabel: "SLA 24h >",
    crSlaBreachAggregate: "{n} SLA prekročené",
    crSlaWarnAggregate: "{n} vo výstrahe (>{h}h)",
    crSlaOkAggregate: "{n} v termíne",
    crSlaBadgeBreach: "SLA prekročené · {h}h od odoslania",
    crSlaBadgeWarn: "Pozor · {h}h · uplynie za {rem}h",
    crSlaBadgeOk: "V termíne · {h}h · {rem}h do termínu",
    crDateTimeLocale: "sk-SK",
  },
  EE: {
    changeRequestsTab: "Muutmistaotlused",
    crTitle: "Klientide andmete muutmise taotlused",
    crSubtitle: "Kliendid on taotlenud lukustatud väljade muutmist. Kinnitage või lükake tagasi.",
    crLoadError: "Viga taotluste laadimisel",
    crApprovedToast: "Taotlus kinnitatud: kliendi andmed uuendatud",
    crApproveError: "Viga kinnitamisel",
    crRejectedToast: "Taotlus tagasi lükatud",
    crRejectError: "Viga tagasi lükkamisel",
    crConfirmApply: 'Kas rakendada „{value}" väljale {field} kliendile {name}?',
    crDeepLinkGone: "E-kirjast lingitud taotlus ei ole enam ootel: see võib olla juba kinnitatud või tagasi lükatud.",
    crDeepLinkBanner: "Avatud otse teate-e-kirjast",
    crEmpty: "Ootel taotlusi pole",
    crEmptyHint: "Klientide taotlused ilmuvad siia.",
    crFieldLabelEmail: "E-post",
    crFieldLabelPhone: "Mobiiltelefon 1",
    crFieldLabelTaxCode: "Maksukood",
    crFieldSection: "Väli",
    crCurrentValue: "Praegune väärtus",
    crNewValueRequested: "Uus soovitud väärtus",
    crCustomerNote: "Kliendi põhjendus",
    crRejectionPlaceholder: "Tagasilükkamise põhjus (valikuline)",
    crRejectBtn: "Lükka tagasi",
    crApproveBtn: "Kinnita",
    crApproveResendBtn: "Kinnita ja saada e-kiri uuesti",
    crApproveWarning: "Kinnitamisel uuendate kliendi {field} välja ja AUTOMAATSELT saadetakse uus aktiveerimise e-kiri aadressile {target}. Vana link muutub kasutuskõlbmatuks.",
    crApproveWarningTargetEmail: "uus aadress",
    crApproveWarningTargetCustomer: "klient",
    crPreSigBadge: "Enne allkirjastamist · Kiireloomuline",
    crPreSigCountOne: "{n} taotlus — kliendid ootavad jätkamist",
    crPreSigCountMany: "{n} taotlust — kliendid ootavad jätkamist",
    crPostSigBadge: "Pärast allkirjastamist",
    crPostSigCountOne: "{n} taotlus — leping juba allkirjastatud",
    crPostSigCountMany: "{n} taotlust — leping juba allkirjastatud",
    crSlaLabel: "SLA 24t >",
    crSlaBreachAggregate: "{n} SLA rikutud",
    crSlaWarnAggregate: "{n} hoiatuses (>{h}t)",
    crSlaOkAggregate: "{n} õigel ajal",
    crSlaBadgeBreach: "SLA rikutud · {h}t esitamisest",
    crSlaBadgeWarn: "Tähelepanu · {h}t · aegub {rem}t pärast",
    crSlaBadgeOk: "Õigel ajal · {h}t · {rem}t tähtajani",
    crDateTimeLocale: "et-EE",
  },
  LT: {
    changeRequestsTab: "Keitimo prašymai",
    crTitle: "Klientų duomenų keitimo prašymai",
    crSubtitle: "Klientai paprašė pakeisti užrakintus laukus. Patvirtinkite arba atmeskite.",
    crLoadError: "Klaida įkeliant prašymus",
    crApprovedToast: "Prašymas patvirtintas: kliento duomenys atnaujinti",
    crApproveError: "Klaida tvirtinant",
    crRejectedToast: "Prašymas atmestas",
    crRejectError: "Klaida atmetant",
    crConfirmApply: 'Ar taikyti „{value}" laukui {field} klientui {name}?',
    crDeepLinkGone: "Iš el. laiško susietas prašymas nebėra laukiantis: jis galėjo būti jau patvirtintas arba atmestas.",
    crDeepLinkBanner: "Atidaryta tiesiogiai iš pranešimo el. laiško",
    crEmpty: "Nėra laukiančių prašymų",
    crEmptyHint: "Klientų prašymai bus rodomi čia.",
    crFieldLabelEmail: "El. paštas",
    crFieldLabelPhone: "Mobilus telefonas 1",
    crFieldLabelTaxCode: "Mokesčių kodas",
    crFieldSection: "Laukas",
    crCurrentValue: "Dabartinė reikšmė",
    crNewValueRequested: "Nauja pageidaujama reikšmė",
    crCustomerNote: "Kliento pagrindimas",
    crRejectionPlaceholder: "Atmetimo priežastis (neprivaloma)",
    crRejectBtn: "Atmesti",
    crApproveBtn: "Patvirtinti",
    crApproveResendBtn: "Patvirtinti ir siųsti el. laišką iš naujo",
    crApproveWarning: "Patvirtinimas atnaujins kliento lauką {field} ir AUTOMATIŠKAI bus išsiųstas naujas aktyvavimo el. laiškas į {target}. Sena nuoroda taps nebetinkama.",
    crApproveWarningTargetEmail: "naujas adresas",
    crApproveWarningTargetCustomer: "klientas",
    crPreSigBadge: "Prieš pasirašymą · Skubu",
    crPreSigCountOne: "{n} prašymas — klientai laukia, kad galėtų tęsti",
    crPreSigCountMany: "{n} prašymai — klientai laukia, kad galėtų tęsti",
    crPostSigBadge: "Po pasirašymo",
    crPostSigCountOne: "{n} prašymas — sutartis jau pasirašyta",
    crPostSigCountMany: "{n} prašymai — sutartis jau pasirašyta",
    crSlaLabel: "SLA 24val >",
    crSlaBreachAggregate: "{n} SLA pažeista",
    crSlaWarnAggregate: "{n} įspėjime (>{h}val)",
    crSlaOkAggregate: "{n} laiku",
    crSlaBadgeBreach: "SLA pažeista · {h}val nuo pateikimo",
    crSlaBadgeWarn: "Dėmesio · {h}val · baigsis po {rem}val",
    crSlaBadgeOk: "Laiku · {h}val · {rem}val iki termino",
    crDateTimeLocale: "lt-LT",
  },
  LV: {
    changeRequestsTab: "Izmaiņu pieprasījumi",
    crTitle: "Klientu datu izmaiņu pieprasījumi",
    crSubtitle: "Klienti ir pieprasījuši izmaiņas bloķētajos laukos. Apstipriniet vai noraidiet.",
    crLoadError: "Kļūda ielādējot pieprasījumus",
    crApprovedToast: "Pieprasījums apstiprināts: klienta dati atjaunināti",
    crApproveError: "Kļūda apstiprināšanā",
    crRejectedToast: "Pieprasījums noraidīts",
    crRejectError: "Kļūda noraidīšanā",
    crConfirmApply: 'Vai piemērot „{value}" laukam {field} klientam {name}?',
    crDeepLinkGone: "Pieprasījums, kas saistīts no e-pasta, vairs nav gaidīšanā: tas var būt jau apstiprināts vai noraidīts.",
    crDeepLinkBanner: "Atvērts tieši no paziņojuma e-pasta",
    crEmpty: "Nav gaidošu pieprasījumu",
    crEmptyHint: "Klientu pieprasījumi parādīsies šeit.",
    crFieldLabelEmail: "E-pasts",
    crFieldLabelPhone: "Mobilais tālrunis 1",
    crFieldLabelTaxCode: "Nodokļu kods",
    crFieldSection: "Lauks",
    crCurrentValue: "Pašreizējā vērtība",
    crNewValueRequested: "Jaunā pieprasītā vērtība",
    crCustomerNote: "Klienta pamatojums",
    crRejectionPlaceholder: "Noraidīšanas iemesls (neobligāts)",
    crRejectBtn: "Noraidīt",
    crApproveBtn: "Apstiprināt",
    crApproveResendBtn: "Apstiprināt un nosūtīt e-pastu vēlreiz",
    crApproveWarning: "Apstiprināšana atjauninās klienta {field} lauku, un AUTOMĀTISKI tiks nosūtīts jauns aktivizēšanas e-pasts uz {target}. Vecā saite kļūs nelietojama.",
    crApproveWarningTargetEmail: "jaunā adrese",
    crApproveWarningTargetCustomer: "klients",
    crPreSigBadge: "Pirms parakstīšanas · Steidzami",
    crPreSigCountOne: "{n} pieprasījums — klienti gaida, lai turpinātu",
    crPreSigCountMany: "{n} pieprasījumi — klienti gaida, lai turpinātu",
    crPostSigBadge: "Pēc parakstīšanas",
    crPostSigCountOne: "{n} pieprasījums — līgums jau parakstīts",
    crPostSigCountMany: "{n} pieprasījumi — līgums jau parakstīts",
    crSlaLabel: "SLA 24st >",
    crSlaBreachAggregate: "{n} SLA pārkāpts",
    crSlaWarnAggregate: "{n} brīdinājumā (>{h}st)",
    crSlaOkAggregate: "{n} laikā",
    crSlaBadgeBreach: "SLA pārkāpts · {h}st kopš iesniegšanas",
    crSlaBadgeWarn: "Uzmanību · {h}st · beidzas pēc {rem}st",
    crSlaBadgeOk: "Laikā · {h}st · {rem}st līdz termiņam",
    crDateTimeLocale: "lv-LV",
  },
  MT: {
    changeRequestsTab: "Talbiet ta' modifika",
    crTitle: "Talbiet ta' modifika tad-dejta tal-klijenti",
    crSubtitle: "Il-klijenti talbu modifiki f'kampi msakkra. Approva jew irrifjuta.",
    crLoadError: "Żball fit-tagħbija tat-talbiet",
    crApprovedToast: "Talba approvata: id-dejta tal-klijent aġġornata",
    crApproveError: "Żball fl-approvazzjoni",
    crRejectedToast: "Talba miċħuda",
    crRejectError: "Żball fiċ-ċaħda",
    crConfirmApply: 'Applika „{value}" fuq {field} tal-klijent {name}?',
    crDeepLinkGone: "It-talba marbuta mill-email m'għadhiex pendenti: tista' tkun diġà ġiet approvata jew miċħuda.",
    crDeepLinkBanner: "Miftuħa direttament mill-email tan-notifika",
    crEmpty: "Ebda talba pendenti",
    crEmptyHint: "It-talbiet tal-klijenti se jidhru hawn.",
    crFieldLabelEmail: "Email",
    crFieldLabelPhone: "Mowbajl 1",
    crFieldLabelTaxCode: "Kodiċi tat-taxxa",
    crFieldSection: "Kamp",
    crCurrentValue: "Valur attwali",
    crNewValueRequested: "Valur ġdid mitlub",
    crCustomerNote: "Motivazzjoni tal-klijent",
    crRejectionPlaceholder: "Raġuni taċ-ċaħda (fakultattiva)",
    crRejectBtn: "Irrifjuta",
    crApproveBtn: "Approva",
    crApproveResendBtn: "Approva u erġa' ibgħat l-email",
    crApproveWarning: "Bl-approvazzjoni se taġġorna l-kamp {field} tal-klijent u AWTOMATIKAMENT jintbagħat email ġdid ta' attivazzjoni lil {target}. Il-link il-qadim se jsir bla użu.",
    crApproveWarningTargetEmail: "l-indirizz il-ġdid",
    crApproveWarningTargetCustomer: "il-klijent",
    crPreSigBadge: "Qabel il-firma · Urġenti",
    crPreSigCountOne: "{n} talba — klijenti jistennew biex jibqgħu għaddejjin",
    crPreSigCountMany: "{n} talbiet — klijenti jistennew biex jibqgħu għaddejjin",
    crPostSigBadge: "Wara l-firma",
    crPostSigCountOne: "{n} talba — kuntratt diġà ffirmat",
    crPostSigCountMany: "{n} talbiet — kuntratt diġà ffirmat",
    crSlaLabel: "SLA 24s >",
    crSlaBreachAggregate: "{n} SLA maqbuż",
    crSlaWarnAggregate: "{n} f'twissija (>{h}s)",
    crSlaOkAggregate: "{n} fil-ħin",
    crSlaBadgeBreach: "SLA maqbuż · {h}s minn meta ntbagħat",
    crSlaBadgeWarn: "Attenzjoni · {h}s · jiskadi fi {rem}s",
    crSlaBadgeOk: "Fil-ħin · {h}s · {rem}s sad-data ta' skadenza",
    crDateTimeLocale: "mt-MT",
  },
  CY: {
    changeRequestsTab: "Αιτήματα τροποποίησης",
    crTitle: "Αιτήματα τροποποίησης δεδομένων πελατών",
    crSubtitle: "Οι πελάτες έχουν ζητήσει αλλαγές σε κλειδωμένα πεδία. Εγκρίνετε ή απορρίψτε.",
    crLoadError: "Σφάλμα κατά τη φόρτωση αιτημάτων",
    crApprovedToast: "Αίτημα εγκρίθηκε: τα στοιχεία του πελάτη ενημερώθηκαν",
    crApproveError: "Σφάλμα κατά την έγκριση",
    crRejectedToast: "Αίτημα απορρίφθηκε",
    crRejectError: "Σφάλμα κατά την απόρριψη",
    crConfirmApply: 'Εφαρμογή του "{value}" στο πεδίο {field} του πελάτη {name};',
    crDeepLinkGone: "Το αίτημα από το e-mail δεν είναι πλέον σε εκκρεμότητα: μπορεί να έχει ήδη εγκριθεί ή απορριφθεί.",
    crDeepLinkBanner: "Άνοιξε απευθείας από το e-mail ειδοποίησης",
    crEmpty: "Δεν υπάρχουν εκκρεμή αιτήματα",
    crEmptyHint: "Τα αιτήματα των πελατών θα εμφανιστούν εδώ.",
    crFieldLabelEmail: "E-mail",
    crFieldLabelPhone: "Κινητό τηλέφωνο 1",
    crFieldLabelTaxCode: "Φορολογικός κωδικός",
    crFieldSection: "Πεδίο",
    crCurrentValue: "Τρέχουσα τιμή",
    crNewValueRequested: "Νέα ζητούμενη τιμή",
    crCustomerNote: "Αιτιολόγηση πελάτη",
    crRejectionPlaceholder: "Αιτιολογία απόρριψης (προαιρετικό)",
    crRejectBtn: "Απόρριψη",
    crApproveBtn: "Έγκριση",
    crApproveResendBtn: "Έγκριση και επανάληψη αποστολής e-mail",
    crApproveWarning: "Με την έγκριση θα ενημερωθεί το πεδίο {field} του πελάτη και θα σταλεί ΑΥΤΟΜΑΤΑ ένα νέο e-mail ενεργοποίησης στο {target}. Ο παλιός σύνδεσμος θα καταστεί άχρηστος.",
    crApproveWarningTargetEmail: "νέα διεύθυνση",
    crApproveWarningTargetCustomer: "πελάτη",
    crPreSigBadge: "Πριν την υπογραφή · Επείγον",
    crPreSigCountOne: "{n} αίτημα — πελάτες περιμένουν να συνεχίσουν",
    crPreSigCountMany: "{n} αιτήματα — πελάτες περιμένουν να συνεχίσουν",
    crPostSigBadge: "Μετά την υπογραφή",
    crPostSigCountOne: "{n} αίτημα — σύμβαση ήδη υπογεγραμμένη",
    crPostSigCountMany: "{n} αιτήματα — σύμβαση ήδη υπογεγραμμένη",
    crSlaLabel: "SLA 24ω >",
    crSlaBreachAggregate: "{n} SLA παραβιάστηκε",
    crSlaWarnAggregate: "{n} σε προειδοποίηση (>{h}ω)",
    crSlaOkAggregate: "{n} εντός χρόνου",
    crSlaBadgeBreach: "SLA παραβιάστηκε · {h}ω από την αποστολή",
    crSlaBadgeWarn: "Προσοχή · {h}ω · λήγει σε {rem}ω",
    crSlaBadgeOk: "Εντός χρόνου · {h}ω · {rem}ω μέχρι τη λήξη",
    crDateTimeLocale: "el-CY",
  },
};

// Helper to interpolate {placeholder} tokens with runtime values.
// Used by DealerChangeRequestsView for the SLA badges, the confirm
// dialog, count strings, and the approve-warning banner.
export const fmtI18n = (template, params = {}) => {
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = params[key];
    return v === undefined || v === null ? "" : String(v);
  });
};

export const getChangeRequestsPack = (langCode) => {
  return CHANGE_REQUESTS_I18N[langCode] || CHANGE_REQUESTS_I18N.UK;
};

export { CHANGE_REQUESTS_I18N };
export default CHANGE_REQUESTS_I18N;
